import { UnauthenticatedError } from "@sp-crm/core";
import React, { FunctionComponent } from "react";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "react-query";
import { useDispatch } from "react-redux";
import { clientLogout } from "store/actions/session";
import { reportError, shouldSkipReporting } from "util/error-tracking";

interface AppProps {
    children: (queryClient: QueryClient) => React.ReactNode;
}

export const StoreAwareQueryClientProvider: FunctionComponent<AppProps> = ({
    children,
}) => {
    const dispatch = useDispatch();

    const queryClient = React.useMemo(
        () =>
            new QueryClient({
                queryCache: new QueryCache({
                    onError: error => {
                        if (error instanceof UnauthenticatedError) {
                            clientLogout(dispatch);
                        }
                    },
                }),
                mutationCache: new MutationCache({
                    onError: (error, _variables, _context, mutation) => {
                        if (error instanceof UnauthenticatedError) {
                            clientLogout(dispatch);
                        }

                        if (!shouldSkipReporting(error)) {
                            const err = error as Error;
                            const mutationKey = mutation.options.mutationKey;
                            const keyString = Array.isArray(mutationKey)
                                ? mutationKey.join(":")
                                : String(mutationKey ?? "unknown");
                            reportError(err, {
                                fingerprint: `mutation:${keyString}`,
                                params: { message: err.message },
                                context: { mutationKey: keyString },
                            });
                        }
                    },
                }),
            }),
        [dispatch],
    );

    return (
        <QueryClientProvider client={queryClient}>
            {/* Useful for debugging react-query <ReactQueryDevtools initialIsOpen={false} /> */}
            {children(queryClient)}
        </QueryClientProvider>
    );
};
